import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/language";
import { useSidebarClock } from "@/contexts/sidebarClock";

type UtcClockProps = {
  /** Tighter layout for mobile header */
  compact?: boolean;
  className?: string;
};

export function UtcClock({ compact, className }: UtcClockProps) {
  const { t, language } = useLanguage();
  const { mode } = useSidebarClock();
  const n = t.nav;
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (mode === "hidden") {
    return null;
  }

  const useLocal = mode === "local";
  const timeZone = useLocal ? undefined : "UTC";
  const dateLocale = language === "fr" ? "fr-CA" : "en-CA";

  const time = now.toLocaleTimeString("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const date = now.toLocaleDateString(dateLocale, {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const label = useLocal ? n.localTimeShort : n.utcTimeShort;
  const labelFull = useLocal ? n.localTime : n.utcTime;
  const title = useLocal ? n.localClockTitle : n.utcClockTitle;

  if (compact) {
    return (
      <div
        className={cn("flex items-center gap-1.5 text-right", className)}
        title={title}
      >
        <Clock className="h-3.5 w-3.5 shrink-0 text-primary/60" aria-hidden />
        <div>
          <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/70">{label}</div>
          <div className="font-mono text-[11px] tabular-nums text-primary leading-none">{time}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mb-3 flex items-center gap-2 rounded-md border border-border/60 bg-secondary/30 px-3 py-2",
        className,
      )}
      title={title}
    >
      <Clock className="h-4 w-4 shrink-0 text-primary/70" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground/60">{labelFull}</div>
        <div className="font-mono text-sm font-semibold tabular-nums text-primary">{time}</div>
        <div className="truncate text-[10px] font-mono text-muted-foreground/70">{date}</div>
      </div>
    </div>
  );
}
